import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { CartDrawer } from "@/components/CartDrawer";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Products } from "@/components/Products";
import { Testimonials } from "@/components/Testimonials";
import { CompraSegura } from "@/components/CompraSegura";
import { SocialPosts } from "@/components/SocialPosts";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { ProductDetail } from "@/pages/ProductDetail";
import { ResetPassword } from "@/pages/ResetPassword";
import { Privacidad } from "@/pages/Privacidad";
import { Blog } from "@/pages/Blog";
import { BlogPost } from "@/pages/BlogPost";
import { Rastreo } from "@/pages/Rastreo";
import { Devoluciones } from "@/pages/Devoluciones";
import { Terminos } from "@/pages/Terminos";
import { Ayuda } from "@/pages/Ayuda";
import { Nosotros } from "@/pages/Nosotros";
import { Contacto } from "@/pages/Contacto";
import { CouponPopup } from "@/components/CouponPopup";
import { scrollToSection } from "@/lib/scroll";
import { fbqTrack } from "@/lib/track";
import { trackVisit } from "@/lib/visits";

const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const Checkout = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.Checkout })));
const PagoExito = lazy(() => import("@/pages/PagoResultado").then((m) => ({ default: m.PagoExito })));
const PagoPendiente = lazy(() => import("@/pages/PagoResultado").then((m) => ({ default: m.PagoPendiente })));
const PagoError = lazy(() => import("@/pages/PagoResultado").then((m) => ({ default: m.PagoError })));

const PageLoader = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-2 border-black/10 border-t-[#FF5508] animate-spin" />
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#nosotros") {
      navigate("/nosotros", { replace: true });
      return;
    }
    if (hash === "#aventuras") {
      navigate("/nosotros#aventuras", { replace: true });
      return;
    }
    if (hash === "#contacto") {
      navigate("/contacto", { replace: true });
      return;
    }
    if (hash.length > 1 && document.getElementById(hash.slice(1))) scrollToSection(hash.slice(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Header />
      <main>
        <Hero />
        <Marquee />
        <Products />
        <Testimonials />
        <CompraSegura />
        <SocialPosts />
      </main>
      <Footer />
      <CouponPopup />
    </>
  );
};

const LenisBridge = () => {
  const lenis = useLenis();
  useEffect(() => {
    window.__lenis = lenis || null;
    return () => {
      if (window.__lenis === lenis) window.__lenis = null;
    };
  }, [lenis]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    fbqTrack("PageView");
    trackVisit(pathname);
  }, [pathname, lenis]);
  return null;
};

const isTouchDevice =
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

function App() {
  const content = (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <LenisBridge />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/tienda" element={<Home />} />
          <Route path="/nosotros" element={<Nosotros />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/ayuda" element={<Ayuda />} />
          <Route path="/producto/:id" element={<ProductDetail />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/restablecer" element={<ResetPassword />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/rastreo" element={<Rastreo />} />
          <Route path="/devoluciones" element={<Devoluciones />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/pago/exito" element={<PagoExito />} />
          <Route path="/pago/pendiente" element={<PagoPendiente />} />
          <Route path="/pago/error" element={<PagoError />} />
        </Routes>
        </Suspense>
        <CartDrawer />
        <WhatsAppFloat />
        <Toaster theme="light" position="bottom-left" />
      </BrowserRouter>
    </div>
  );
  return (
    <CartProvider>
      {isTouchDevice ? (
        content
      ) : (
        <ReactLenis root options={{ lerp: 0.09, wheelMultiplier: 1.05 }}>
          {content}
        </ReactLenis>
      )}
    </CartProvider>
  );
}

export default App;
